/* eslint-disable i18next/no-literal-string */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Card, CardContent } from 'src/components/Card';
import { Divider } from 'src/components/Divider';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';

export default function DialogScrollTest(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [groupId, setGroupId] = useState(searchParams.get('groupId') || '');

  const handleGroupIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newGroupId = event.target.value;
    setGroupId(newGroupId);

    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    if (newGroupId) {
      newSearchParams.set('groupId', newGroupId);
    } else {
      newSearchParams.delete('groupId');
    }
    setSearchParams(newSearchParams);
  };

  return (
    <>
      <AppFrameConfig
        id="dialog-scroll-test"
        title="Dialog Scroll Position Test"
      />

      <Stack direction="column" gap={4} my={4}>
        {/* Group ID Input */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🎯 Group Discussion Iframe Test
            </Typography>
            <TextField
              label="Group ID"
              value={groupId}
              onChange={handleGroupIdChange}
              placeholder="Enter group ID (e.g., 202)"
              helperText="Enter a group ID to test dialog positioning within the group discussion iframe"
              fullWidth
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" gutterBottom>
              <strong>URL:</strong>{' '}
              {groupId
                ? `${window.location.origin}/ex4/groups/${groupId}/discussion?nochrome=true`
                : 'Enter a group ID above'}
            </Typography>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📝 Testing Instructions
            </Typography>
            <Typography variant="body2" gutterBottom>
              1. <strong>Enter a group ID above</strong> to load the group
              discussion in an iframe
            </Typography>
            <Typography variant="body2" gutterBottom>
              2. <strong>Scroll within the iframe below</strong> - this will
              trigger scroll tracking
            </Typography>
            <Typography variant="body2" gutterBottom>
              3. <strong>Scroll this parent page</strong> - this will also
              affect dialog positioning
            </Typography>
            <Typography variant="body2" gutterBottom>
              4. <strong>Try to delete a post in the iframe</strong> - dialog
              should position based on combined scroll offset
            </Typography>
            <Typography variant="body2" gutterBottom>
              5. <strong>Open browser console</strong> to see debug logs from
              scroll tracking in the iframe
            </Typography>
          </CardContent>
        </Card>

        <Divider />

        {/* Group Discussion Iframe */}
        {groupId ? (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📱 Group Discussion Iframe
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                This iframe loads the group discussion page. Try scrolling
                within it and triggering dialogs (like deleting posts).
              </Typography>
              <Box
                component="iframe"
                src={`${window.location.origin}/ex4/groups/${groupId}/discussion?nochrome=true`}
                sx={{
                  width: '100%',
                  height: '600px',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
                title={`Group ${groupId} Discussion`}
              />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📱 Group Discussion Iframe
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter a group ID above to load the group discussion iframe for
                testing.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Additional scrollable content for parent page scrolling */}
        <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
          Additional Parent Content
        </Typography>
        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          Scroll through this content to test parent page scrolling while the
          iframe is also scrollable.
        </Typography>

        {Array.from({ length: 3 }, (_, i) => (
          <Card key={i} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Parent Content Block #{i + 1}
              </Typography>
              <Typography variant="body2" gutterBottom>
                This is parent page content block #{i + 1}. Scroll through this
                content to test how parent page scrolling affects dialog
                positioning in the iframe above. Lorem ipsum dolor sit amet,
                consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut
                labore et dolore magna aliqua.
              </Typography>
            </CardContent>
          </Card>
        ))}

        {/* Bottom spacer */}
        <Box height="300px" bgcolor="background.level2" p={4}>
          <Typography variant="h6" gutterBottom>
            🎯 End of Parent Content
          </Typography>
          <Typography variant="body2">
            You&apos;ve reached the bottom of the parent page! The iframe above
            should still be scrollable independently. Try triggering dialogs
            from the iframe to test positioning at different scroll levels.
          </Typography>
        </Box>
      </Stack>
    </>
  );
}
